import React, { useState, useRef, useEffect, useCallback } from 'react';
import './UserDashboard.css';

interface UserDashboardProps {
  user: any;
  sessionToken: string;
  onProfileUpdate: (updatedUser: any) => void;
  onLogout: () => void;
  onNavigate: (path: string) => void;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '/api';

const UserDashboard: React.FC<UserDashboardProps> = ({
  user,
  sessionToken,
  onProfileUpdate,
  onLogout,
  onNavigate
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [editMode, setEditMode] = useState(false);
  const [username, setUsername] = useState(user?.username || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalTrades: user?.total_trades || 0,
    totalVolume: user?.total_volume || 0,
    favoriteTokens: user?.favorite_tokens || []
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadTransactions = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/transactions/my`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        const fetchedTransactions = result.transactions || [];
        setTransactions(fetchedTransactions);

        const totalTrades = fetchedTransactions.length;
        const totalVolume = fetchedTransactions.reduce((acc: number, tx: any) => {
          const value = typeof tx.suiAmount === 'number' ? tx.suiAmount : Number(tx.suiAmount ?? 0);
          return acc + (Number.isFinite(value) ? value : 0);
        }, 0);

        setStats({
          totalTrades,
          totalVolume: totalVolume || user?.total_volume || 0,
          favoriteTokens: user?.favorite_tokens || []
        });
      }
    } catch (err) {
      console.error('Failed to load transactions:', err);
    }
  }, [sessionToken, user?.favorite_tokens, user?.total_volume]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile) return avatarPreview;

    const formData = new FormData();
    formData.append('file', avatarFile);

    try {
      const response = await fetch(`${BACKEND_URL}/uploads/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        return result.url;
      }
    } catch (err) {
      console.error('Avatar upload failed:', err);
    }
    return avatarPreview;
  };

  const handleProfileUpdate = async () => {
    setLoading(true);

    try {
      const avatarUrl = await uploadAvatar();

      const response = await fetch(`${BACKEND_URL}/auth/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          username: username.trim(),
          bio: bio.trim(),
          avatar: avatarUrl
        })
      });

      if (response.ok) {
        const result = await response.json();
        const updatedUser = result.user;
        
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        onProfileUpdate(updatedUser);
        setEditMode(false);
        setAvatarFile(null);
      }
    } catch (err) {
      console.error('Profile update failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1000000) return `${(volume / 1000000).toFixed(2)}M`;
    if (volume >= 1000) return `${(volume / 1000).toFixed(2)}K`;
    return volume.toFixed(2);
  };

  return (
    <div className="user-dashboard">
      <div className="dashboard-header">
        <h1>User Dashboard</h1>
        <button className="back-button" onClick={() => onNavigate('/')}>
          Back to App
        </button>
      </div>

      <div className="dashboard-content">
        <div className="profile-section">
          <div className="profile-card">
            <div className="profile-avatar-section">
              <div className="profile-avatar" onClick={() => editMode && fileInputRef.current?.click()}>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" />
                ) : (
                  <div className="avatar-placeholder">
                    {username ? username[0].toUpperCase() : 'U'}
                  </div>
                )}
                {editMode && (
                  <div className="avatar-overlay">
                    <span>📷 Change</span>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            {editMode ? (
              <div className="edit-profile-form">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username"
                  className="profile-input"
                />
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Bio"
                  className="profile-textarea"
                  rows={3}
                />
                <div className="edit-buttons">
                  <button
                    className="save-button"
                    onClick={handleProfileUpdate}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className="cancel-button"
                    onClick={() => {
                      setEditMode(false);
                      setUsername(user?.username || '');
                      setBio(user?.bio || '');
                      setAvatarPreview(user?.avatar || null);
                      setAvatarFile(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="profile-info">
                <h2>{username || 'Anonymous'}</h2>
                <p className="bio">{bio || 'No bio yet'}</p>
                <div className="wallet-info">
                  <span className="label">Wallet:</span>
                  <span className="value">{formatAddress(user?.wallet_address || '')}</span>
                </div>
                <button className="edit-button" onClick={() => setEditMode(true)}>
                  ✏️ Edit Profile
                </button>
              </div>
            )}

            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-value">{stats.totalTrades}</span>
                <span className="stat-label">TRADES</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">${formatVolume(stats.totalVolume)}</span>
                <span className="stat-label">VOLUME</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{stats.favoriteTokens.length}</span>
                <span className="stat-label">FAVORITES</span>
              </div>
            </div>
          </div>
        </div>

        <div className="activity-section">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`tab ${activeTab === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveTab('transactions')}
            >
              Transactions
            </button>
            <button
              className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'overview' && (
              <div className="overview-content">
                <h3>Welcome back, {username || 'User'}!</h3>
                <div className="quick-stats">
                  <div className="quick-stat-card">
                    <h4>Recent Activity</h4>
                    <p>You've made {stats.totalTrades} trades with a total volume of ${formatVolume(stats.totalVolume)}</p>
                  </div>
                  <div className="quick-stat-card">
                    <h4>Account Status</h4>
                    <p className="status-active">✅ Active</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'transactions' && (
              <div className="transactions-content">
                <h3>Transaction History</h3>
                {transactions.length > 0 ? (
                  <div className="transactions-list">
                    {transactions.map((tx, index) => (
                      <div key={index} className="transaction-item">
                        <div className="tx-type">
                          <span className={`tx-badge ${tx.transactionType}`}>
                            {tx.transactionType.toUpperCase()}
                          </span>
                        </div>
                        <div className="tx-details">
                          <span className="tx-token">{tx.tokenSymbol}</span>
                          <span className="tx-amount">{tx.suiAmount} SUI</span>
                        </div>
                        <div className="tx-time">
                          {new Date(tx.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-transactions">No transactions yet</p>
                )}
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="settings-content">
                <h3>Account Settings</h3>
                <div className="settings-section">
                  <h4>Security</h4>
                  <button className="settings-button" onClick={onLogout}>
                    🚪 Disconnect Wallet
                  </button>
                </div>
                <div className="settings-section">
                  <h4>Privacy</h4>
                  <p>Your wallet address is partially hidden for privacy</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
